import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Publish as PublishIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  TableChart as TableChartIcon,
  Speed as GaugeIcon,
  Score as ScorecardIcon,
  DonutLarge as DonutIcon,
  TrendingUp as AreaChartIcon,
  Storage as DataSourceIcon,
} from '@mui/icons-material';
import {
  useGetDashboardByIdQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useGetLayoutsQuery,
  useGetLayoutByIdQuery,
  useGetDataSourcesQuery,
  useGetDataSourceMetricsQuery,
  useGetDataSourceDimensionsQuery,
  Dashboard,
  DashboardWidget,
  DashboardLayout,
  WidgetType,
  WidgetConfig,
  WidgetPosition,
  DataSource,
  MetricDefinition,
  DimensionDefinition,
} from '../../../redux/wallboardApi';
import { useAppSelector } from '../../../redux/hooks';
import { selectCurrentUser } from '../../../redux/reducers/authSlice';
import PageWithHeader from '../../common/PageWithHeader';

const STEPS = ['Basic Info', 'Select Layout', 'Add Widgets', 'Review & Save'];

// Widget type configurations
const WIDGET_TYPES: { type: WidgetType; label: string; icon: React.ReactNode; description: string }[] = [
  { type: 'scorecard', label: 'Scorecard', icon: <ScorecardIcon />, description: 'Single metric with comparison' },
  { type: 'piechart', label: 'Pie Chart', icon: <PieChartIcon />, description: 'Show proportions' },
  { type: 'donutchart', label: 'Donut Chart', icon: <DonutIcon />, description: 'Pie with center value' },
  { type: 'bargraph', label: 'Bar Chart', icon: <BarChartIcon />, description: 'Compare values' },
  { type: 'linegraph', label: 'Line Chart', icon: <LineChartIcon />, description: 'Trends over time' },
  { type: 'areachart', label: 'Area Chart', icon: <AreaChartIcon />, description: 'Filled line chart' },
  { type: 'table', label: 'Data Table', icon: <TableChartIcon />, description: 'Tabular data' },
  { type: 'gauge', label: 'Gauge', icon: <GaugeIcon />, description: 'Progress indicator' },
];

const AGGREGATION_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
];

/**
 * Dashboard Builder Page
 * 
 * Multi-step wizard for creating and editing dashboards.
 */
export const DashboardBuilderPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isEditMode = !!id;

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<Partial<Dashboard>>({
    name: '',
    description: '',
    layoutId: '',
    widgets: [],
    refreshInterval: 60000,
    isPublished: false,
    permissions: { roles: [], users: [] },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Queries and mutations
  const { data: existingDashboard, isLoading: isLoadingDashboard } = useGetDashboardByIdQuery(
    id!,
    { skip: !id }
  );
  const { data: layouts, isLoading: isLoadingLayouts } = useGetLayoutsQuery();
  const [createDashboard, { isLoading: isCreating }] = useCreateDashboardMutation();
  const [updateDashboard, { isLoading: isUpdating }] = useUpdateDashboardMutation();

  // Load existing dashboard data
  useEffect(() => {
    if (existingDashboard) {
      setFormData({
        name: existingDashboard.name,
        description: existingDashboard.description,
        layoutId: existingDashboard.layoutId,
        widgets: existingDashboard.widgets,
        refreshInterval: existingDashboard.refreshInterval,
        isPublished: existingDashboard.isPublished,
        permissions: existingDashboard.permissions,
        metadata: existingDashboard.metadata,
      });
    }
  }, [existingDashboard]);

  // Handlers
  const handleNext = () => {
    if (validateStep()) {
      setActiveStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const validateStep = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (activeStep === 0) {
      if (!formData.name?.trim()) {
        newErrors.name = 'Dashboard name is required';
      }
    }

    if (activeStep === 1) {
      if (!formData.layoutId) {
        newErrors.layoutId = 'Please select a layout';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateStep()) return;

    try {
      if (isEditMode && id) {
        await updateDashboard({ id, body: formData }).unwrap();
      } else {
        await createDashboard(formData).unwrap();
      }
      navigate('/wallboard');
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      setErrors({ submit: 'Failed to save dashboard. Please try again.' });
    }
  };

  const updateFormData = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Loading state
  if (isLoadingDashboard) {
    return (
      <PageWithHeader>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/wallboard')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5">
            {isEditMode ? 'Edit Dashboard' : 'Create New Dashboard'}
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Step Content */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {activeStep === 0 && (
            <BasicInfoStep
              formData={formData}
              errors={errors}
              onChange={updateFormData}
            />
          )}

          {activeStep === 1 && (
            <LayoutSelectStep
              layouts={layouts || []}
              selectedLayoutId={formData.layoutId || ''}
              isLoading={isLoadingLayouts}
              onChange={(layoutId) => updateFormData('layoutId', layoutId)}
              error={errors.layoutId}
            />
          )}

          {activeStep === 2 && (
            <WidgetsStep
              widgets={formData.widgets || []}
              layoutId={formData.layoutId || ''}
              onChange={(widgets) => updateFormData('widgets', widgets)}
            />
          )}

          {activeStep === 3 && (
            <ReviewStep
              formData={formData}
              layouts={layouts || []}
            />
          )}
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === STEPS.length - 1 ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    updateFormData('isPublished', false);
                    handleSave();
                  }}
                  disabled={isCreating || isUpdating}
                  startIcon={<SaveIcon />}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    updateFormData('isPublished', true);
                    handleSave();
                  }}
                  disabled={isCreating || isUpdating}
                  startIcon={isCreating || isUpdating ? <CircularProgress size={20} /> : <PublishIcon />}
                >
                  {isEditMode ? 'Save & Publish' : 'Create & Publish'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </PageWithHeader>
  );
};

/**
 * Step 1: Basic Info
 */
interface BasicInfoStepProps {
  formData: Partial<Dashboard>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ formData, errors, onChange }) => {
  const refreshIntervalOptions = [
    { value: 5000, label: '5 seconds' },
    { value: 15000, label: '15 seconds' },
    { value: 30000, label: '30 seconds' },
    { value: 60000, label: '1 minute' },
    { value: 300000, label: '5 minutes' },
    { value: 600000, label: '10 minutes' },
    { value: 900000, label: '15 minutes' },
    { value: 1800000, label: '30 minutes' },
    { value: 3600000, label: '1 hour' },
  ];

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>

      <TextField
        fullWidth
        label="Dashboard Name"
        value={formData.name || ''}
        onChange={(e) => onChange('name', e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        sx={{ mb: 3 }}
        required
      />

      <TextField
        fullWidth
        label="Description"
        value={formData.description || ''}
        onChange={(e) => onChange('description', e.target.value)}
        multiline
        rows={3}
        sx={{ mb: 3 }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Auto-Refresh Interval</InputLabel>
        <Select
          value={formData.refreshInterval || 60000}
          label="Auto-Refresh Interval"
          onChange={(e) => onChange('refreshInterval', e.target.value)}
        >
          {refreshIntervalOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Category"
        value={formData.metadata?.category || ''}
        onChange={(e) => onChange('metadata', { ...formData.metadata, category: e.target.value })}
        placeholder="e.g., Procurement, Finance, Operations"
        sx={{ mb: 3 }}
      />
    </Box>
  );
};

/**
 * Step 2: Layout Selection
 */
interface LayoutSelectStepProps {
  layouts: DashboardLayout[];
  selectedLayoutId: string;
  isLoading: boolean;
  onChange: (layoutId: string) => void;
  error?: string;
}

const LayoutSelectStep: React.FC<LayoutSelectStepProps> = ({
  layouts,
  selectedLayoutId,
  isLoading,
  onChange,
  error,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select a Layout
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a layout template for your dashboard. This determines how widgets are arranged.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {layouts.map((layout) => (
          <Grid item xs={12} sm={6} md={4} key={layout.id}>
            <Card
              sx={{
                border: selectedLayoutId === layout.id ? 2 : 1,
                borderColor: selectedLayoutId === layout.id ? 'primary.main' : 'divider',
                transition: 'border-color 0.2s',
              }}
            >
              <CardActionArea onClick={() => onChange(layout.id)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1">{layout.name}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {layout.description || 'No description'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={`Max ${layout.maxWidgets} widgets`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${layout.gridDefinition.columns}×${layout.gridDefinition.rows}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/**
 * Step 3: Widgets Builder
 */
interface WidgetsStepProps {
  widgets: DashboardWidget[];
  layoutId: string;
  onChange: (widgets: DashboardWidget[]) => void;
}

const WidgetsStep: React.FC<WidgetsStepProps> = ({ widgets, layoutId, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const [dialogStep, setDialogStep] = useState<'type' | 'config'>('type');
  
  // Widget form state
  const [widgetType, setWidgetType] = useState<WidgetType>('bargraph');
  const [widgetName, setWidgetName] = useState('');
  const [dataSourceId, setDataSourceId] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('');
  const [aggregation, setAggregation] = useState<string>('sum');
  const [groupBy, setGroupBy] = useState('');
  const [limit, setLimit] = useState(10);

  // Fetch data sources
  const { data: dataSources = [], isLoading: isLoadingDataSources } = useGetDataSourcesQuery();
  
  // Fetch layout to get grid definition
  const { data: layout } = useGetLayoutByIdQuery(layoutId, { skip: !layoutId });
  
  // Fetch metrics for selected data source
  const { data: metrics = [] } = useGetDataSourceMetricsQuery(dataSourceId, {
    skip: !dataSourceId,
  });
  
  // Fetch dimensions for selected data source
  const { data: dimensions = [] } = useGetDataSourceDimensionsQuery(dataSourceId, {
    skip: !dataSourceId,
  });

  const resetForm = () => {
    setWidgetType('bargraph');
    setWidgetName('');
    setDataSourceId('');
    setSelectedMetric('');
    setAggregation('sum');
    setGroupBy('');
    setLimit(10);
    setDialogStep('type');
    setEditingWidget(null);
  };

  const handleOpenDialog = (widget?: DashboardWidget) => {
    if (widget) {
      // Edit mode
      setEditingWidget(widget);
      setWidgetType(widget.type);
      setWidgetName(widget.name);
      setDataSourceId(widget.dataSource);
      setSelectedMetric(widget.config.metric || '');
      setAggregation(widget.config.aggregation || 'sum');
      setGroupBy(widget.config.groupBy || '');
      setLimit(widget.config.limit || 10);
      setDialogStep('config');
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSelectType = (type: WidgetType) => {
    setWidgetType(type);
    const typeConfig = WIDGET_TYPES.find(t => t.type === type);
    setWidgetName(typeConfig?.label || 'New Widget');
    setDialogStep('config');
  };

  // Calculate next available position on grid
  const getNextPosition = (): WidgetPosition => {
    const gridCols = layout?.gridDefinition.columns || 12;
    const existingWidgets = widgets.length;
    
    // Simple layout: 2 widgets per row, each taking half width
    const widgetsPerRow = 2;
    const widgetWidth = Math.floor(gridCols / widgetsPerRow);
    const row = Math.floor(existingWidgets / widgetsPerRow);
    const col = (existingWidgets % widgetsPerRow) * widgetWidth;
    
    return {
      x: col,
      y: row * 2, // Each widget is 2 rows tall
      width: widgetWidth,
      height: 2,
    };
  };

  const handleSaveWidget = () => {
    const metricDef = metrics.find(m => m.id === selectedMetric);
    
    const widgetConfig: WidgetConfig = {
      metric: selectedMetric,
      aggregation: aggregation as any,
      groupBy: groupBy || undefined,
      limit,
      displayOptions: {
        showLegend: ['piechart', 'donutchart', 'linegraph', 'areachart'].includes(widgetType),
      },
    };

    if (editingWidget) {
      // Update existing widget
      const updatedWidgets = widgets.map(w =>
        w.id === editingWidget.id
          ? {
              ...w,
              name: widgetName,
              type: widgetType,
              dataSource: dataSourceId,
              config: widgetConfig,
            }
          : w
      );
      onChange(updatedWidgets);
    } else {
      // Create new widget
      const newWidget: DashboardWidget = {
        id: `widget-${Date.now()}`,
        name: widgetName,
        type: widgetType,
        position: getNextPosition(),
        dataSource: dataSourceId,
        config: widgetConfig,
        description: metricDef?.description,
      };
      onChange([...widgets, newWidget]);
    }

    handleCloseDialog();
  };

  const handleDeleteWidget = (widgetId: string) => {
    onChange(widgets.filter(w => w.id !== widgetId));
  };

  const getWidgetIcon = (type: WidgetType) => {
    const config = WIDGET_TYPES.find(t => t.type === type);
    return config?.icon || <DashboardIcon />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Add Widgets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the widgets for your dashboard.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Widget
        </Button>
      </Box>

      {widgets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <DashboardIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No widgets yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click "Add Widget" to start building your dashboard
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Your First Widget
          </Button>
        </Paper>
      ) : (
        <List>
          {widgets.map((widget, index) => (
            <React.Fragment key={widget.id}>
              {index > 0 && <Divider />}
              <ListItem>
                <ListItemIcon>{getWidgetIcon(widget.type)}</ListItemIcon>
                <ListItemText
                  primary={widget.name}
                  secondary={
                    <Box component="span" sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={WIDGET_TYPES.find(t => t.type === widget.type)?.label}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={dataSources.find(ds => ds.id === widget.dataSource)?.name || widget.dataSource}
                        size="small"
                        variant="outlined"
                        icon={<DataSourceIcon />}
                      />
                      {widget.config.metric && (
                        <Chip label={widget.config.metric} size="small" />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleOpenDialog(widget)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDeleteWidget(widget.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Total widgets: {widgets.length}
        {layout?.maxWidgets && ` / ${layout.maxWidgets} max`}
      </Typography>

      {/* Widget Builder Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingWidget ? 'Edit Widget' : dialogStep === 'type' ? 'Select Widget Type' : 'Configure Widget'}
        </DialogTitle>
        <DialogContent>
          {dialogStep === 'type' ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {WIDGET_TYPES.map(({ type, label, icon, description }) => (
                <Grid item xs={6} sm={4} key={type}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                    }}
                    onClick={() => handleSelectType(type)}
                  >
                    <CardActionArea sx={{ p: 2, textAlign: 'center' }}>
                      <Box sx={{ color: 'primary.main', mb: 1 }}>{icon}</Box>
                      <Typography variant="subtitle2">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {description}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Widget Name"
                value={widgetName}
                onChange={(e) => setWidgetName(e.target.value)}
                fullWidth
                required
              />

              <FormControl fullWidth required>
                <InputLabel>Data Source</InputLabel>
                <Select
                  value={dataSourceId}
                  onChange={(e) => {
                    setDataSourceId(e.target.value);
                    setSelectedMetric('');
                    setGroupBy('');
                  }}
                  label="Data Source"
                >
                  {isLoadingDataSources ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : dataSources.length === 0 ? (
                    <MenuItem disabled>No data sources available</MenuItem>
                  ) : (
                    dataSources.map((ds) => (
                      <MenuItem key={ds.id} value={ds.id}>
                        {ds.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {dataSourceId && (
                <>
                  <FormControl fullWidth required>
                    <InputLabel>Metric</InputLabel>
                    <Select
                      value={selectedMetric}
                      onChange={(e) => setSelectedMetric(e.target.value)}
                      label="Metric"
                    >
                      {metrics.length === 0 ? (
                        <MenuItem disabled>No metrics available</MenuItem>
                      ) : (
                        metrics.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Aggregation</InputLabel>
                    <Select
                      value={aggregation}
                      onChange={(e) => setAggregation(e.target.value)}
                      label="Aggregation"
                    >
                      {AGGREGATION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!['scorecard', 'gauge'].includes(widgetType) && (
                    <FormControl fullWidth>
                      <InputLabel>Group By (optional)</InputLabel>
                      <Select
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value)}
                        label="Group By (optional)"
                      >
                        <MenuItem value="">None</MenuItem>
                        {dimensions.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {['table', 'bargraph', 'piechart', 'donutchart'].includes(widgetType) && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        Limit: {limit}
                      </Typography>
                      <Slider
                        value={limit}
                        onChange={(_, value) => setLimit(value as number)}
                        min={1}
                        max={50}
                        marks={[
                          { value: 5, label: '5' },
                          { value: 10, label: '10' },
                          { value: 25, label: '25' },
                          { value: 50, label: '50' },
                        ]}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {dialogStep === 'config' && !editingWidget && (
            <Button onClick={() => setDialogStep('type')}>Back</Button>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogStep === 'config' && (
            <Button
              variant="contained"
              onClick={handleSaveWidget}
              disabled={!widgetName || !dataSourceId || !selectedMetric}
            >
              {editingWidget ? 'Update' : 'Add'} Widget
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Step 4: Review
 */
interface ReviewStepProps {
  formData: Partial<Dashboard>;
  layouts: DashboardLayout[];
}

const ReviewStep: React.FC<ReviewStepProps> = ({ formData, layouts }) => {
  const selectedLayout = layouts.find(l => l.id === formData.layoutId);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Dashboard
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">
            Dashboard Name
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {formData.name || '-'}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {formData.description || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary">
            Layout
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {selectedLayout?.name || '-'}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            Refresh Interval
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {formData.refreshInterval ? `${formData.refreshInterval / 1000} seconds` : '-'}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            Widgets
          </Typography>
          <Typography variant="body1">
            {formData.widgets?.length || 0} widgets configured
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardBuilderPage;
